import { signal, OnDestroy, Directive, computed } from '@angular/core';
import { loadConnectAndInitialize, StripeConnectInstance } from '@stripe/connect-js';
import { environment } from '../../../environments/environment';

/**
 * Base class for Stripe Connect components
 * Provides common functionality for initializing and managing Stripe embedded components
 */
@Directive()
export abstract class BaseStripeComponentClass implements OnDestroy {
  
  // Stripe Connect instance
  protected stripeConnectInstance?: StripeConnectInstance;
  protected stripeComponent: any;

  // Common state signals
  public isInitializing = signal(false);
  public errorMessage = signal('');

  // Computed properties
  public hasError = computed(() => this.errorMessage().length > 0);

  constructor() {}

  /**
   * Initialize Stripe Connect with embedded components
   * @param clientSecret - The account session client secret from backend
   * @param appearance - Optional appearance customization
   */
  protected async initializeStripeConnect(
    clientSecret: string,
    appearance?: any
  ): Promise<StripeConnectInstance> {
    const publishableKey = environment.stripePublishableKey;

    if (!publishableKey) {
      throw new Error('Stripe publishable key not configured');
    }

    const defaultAppearance = {
      overlays: 'dialog' as const,
      variables: {
        colorPrimary: '#0d6efd',
        colorBackground: '#ffffff',
        colorText: '#212529',
        borderRadius: '0.375rem',
        fontFamily: 'Inter, system-ui, -apple-system, sans-serif'
      }
    };

    this.stripeConnectInstance = await loadConnectAndInitialize({
      publishableKey,
      fetchClientSecret: async () => clientSecret,
      appearance: appearance || defaultAppearance
    });

    return this.stripeConnectInstance;
  }

  /**
   * Create a Stripe embedded component
   * @param componentType - Type of component (e.g., 'account-onboarding', 'payments', etc.)
   */
  protected createStripeComponent(componentType: string): any {
    if (!this.stripeConnectInstance) {
      throw new Error('Stripe Connect not initialized. Call initializeStripeConnect first.');
    }

    this.stripeComponent = this.stripeConnectInstance.create(componentType as any);
    
    if (!this.stripeComponent) {
      throw new Error(`Failed to create Stripe ${componentType} component`);
    }

    return this.stripeComponent;
  }

  /**
   * Append the Stripe component to a container element
   * @param container - The HTML element to append the component to
   */
  protected appendStripeComponent(container: HTMLElement): void {
    if (!this.stripeComponent) {
      throw new Error('Stripe component not created. Call createStripeComponent first.');
    }

    container.appendChild(this.stripeComponent);
  }

  /**
   * Set collection options for components that support it
   * @param options - Collection options (e.g., fields, futureRequirements)
   */
  protected setCollectionOptions(options: any): void {
    if (this.stripeComponent && typeof this.stripeComponent.setCollectionOptions === 'function') {
      this.stripeComponent.setCollectionOptions(options);
    }
  }

  /**
   * Clean up Stripe component
   */
  protected cleanupStripeComponent(): void {
    if (this.stripeComponent) {
      try {
        // Remove the web component from the DOM
        if (this.stripeComponent.parentNode) {
          this.stripeComponent.parentNode.removeChild(this.stripeComponent);
        } else if (this.stripeComponent.remove) {
          this.stripeComponent.remove();
        }
      } catch (error) {
        console.error('Error removing Stripe component:', error);
      }
      this.stripeComponent = null;
    }
  }

  /**
   * Set error message
   */
  protected setError(message: string): void {
    this.errorMessage.set(message);
  }

  /**
   * Clear error message
   */
  public clearError(): void {
    this.errorMessage.set('');
  }

  /**
   * Handle Stripe initialization errors
   */
  protected handleStripeError(error: any): void {
    console.error('Stripe error:', error);
    const message = error?.message || 'An error occurred with Stripe. Please try again.';
    this.setError(message);
  }

  /**
   * Cleanup on component destroy
   */
  ngOnDestroy(): void {
    this.cleanupStripeComponent();
  }
}